import type { ReactNode } from "react"

import { BrandLogo } from "@/components/brand-logo"

import OnboardingProgress from "./onboarding-progress"

export interface OnboardingShellBrand {
  name: string
  logo_url: string | null
}

export interface OnboardingShellProps {
  /** Branded chrome — shown in the header. Crew is NOT white-label; the
   *  brand surfaces as data (logo + name), never theme tokens. */
  brand: OnboardingShellBrand
  /** 1-indexed step number (`null` on terminal screens — chrome still shows
   *  brand identity but hides the progress indicator). */
  step: number | null
  /** Total steps (always 4 for OKS-22 v1). */
  total: number
  /** Step content. Rendered below the chrome inside the centered card. */
  children: ReactNode
}

/**
 * OnboardingShell — branded chrome wrapper for every step of the
 * OKS-22 signup flow.
 *
 * Layout (UX Spec §0):
 *   - Centered single-column card on tablet+ (max-w-md), full-bleed on
 *     mobile (≤640px).
 *   - Persistent header at the top: brand logo (32px) + brand name +
 *     "X of N" progress indicator.
 *   - Children render directly underneath the chrome — step components
 *     own their own internal layout (form fieldsets, charter body, etc).
 *
 * The shell renders brand identity AS DATA, not via theme tokens — Crew
 * is OKS-branded, not white-label. The brand logo and name surface so
 * the creator knows whose crew they're joining; nothing else changes
 * visually based on brand.
 */
export function OnboardingShell({
  brand,
  step,
  total,
  children,
}: OnboardingShellProps) {
  return (
    <div
      className="bg-background flex min-h-svh flex-col"
      data-testid="onboarding-shell"
    >
      <header className="border-border/40 flex flex-col items-center gap-3 border-b px-6 pt-6 pb-4">
        <div className="flex items-center gap-2">
          {/*
            OKS-303 — `<BrandLogo>` (TRD KD-2) renders the `<img>` AND the
            `BrandInitialMonogram` fallback (`onError` swap, UX Spec §3).
            The `onboarding-shell-brand-logo` testid is shared with
            `<WizardShell>` (TRD KD-3) so a single Playwright selector
            covers all 5 onboarding step surfaces. `text-sm` keeps the
            ~4:1 ratio at the 32px tile (OKS-266 pairing).
          */}
          <BrandLogo
            logoUrl={brand.logo_url}
            name={brand.name}
            className="size-8 rounded-md"
            monogramClassName="text-sm"
            testId="onboarding-shell-brand-logo"
          />

          <span
            className="text-foreground text-sm font-medium"
            data-testid="brand-chrome-name"
          >
            {brand.name}
          </span>
        </div>
        <OnboardingProgress step={step} total={total} />
      </header>

      <main
        // tabIndex=-1 lets each step's onMount focus the heading without
        // moving focus to the global landmark — a11y pattern shared with
        // OKS-21's invite-landing.
        tabIndex={-1}
        className="mx-auto flex w-full max-w-md flex-1 flex-col px-6 py-6"
      >
        {children}
      </main>
    </div>
  )
}

export default OnboardingShell
